import UseAxios from '../http/http';
import { serviceName } from '../resources/serviceConstants';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../resources/serviceURLs';
import { FILTER_ASSIGNED_TO_ME } from '../../Constants/strings';
import { STAFFING_REQUEST,
  STAFFING_REQUEST_APPROVAL_ENDPOINT,
  STAFFING_REQUEST_BULK_APPROVAL_ENDPOINT,
  STAFFING_REQUEST_BULK_CLOSE_ENDPOINT,
  STAFFING_REQUEST_CLOSE_ENDPOINT,
  STAFFING_REQUEST_CREATE,
  STAFFING_REQUEST_META} from '../resources/endPoint';
import { getRoleExperienceMappings } from './projectDetails';

const defaultHeaders = { 'Content-Type': 'application/json' };

export const getStaffingRequestBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${STAFFING_REQUEST}`;
  }

  return `${baseUrl}:${servicePort}${serviceURL}/api${STAFFING_REQUEST}`;
};

export const buildQuery = (filters) => {
  const query = new URLSearchParams();
  const approvers = Array.isArray(filters.approvers) ? filters.approvers : [];
  const includesAssignedToMe = approvers.some((approver) => String(approver) === FILTER_ASSIGNED_TO_ME);
  const namedApprovers = approvers.filter((approver) => String(approver) !== FILTER_ASSIGNED_TO_ME);

  // Map UI filter shape to backend query params
  // projectIds is an array; backend accepts single project_id — use first if present
  if (Array.isArray(filters.projectIds) && filters.projectIds.length) {
    filters.projectIds.forEach((id) => query.append('project_id', String(id)));
  }

  // verticals is an array — backend supports a single vertical string; use first selected
  if (Array.isArray(filters.verticals) && filters.verticals.length) {
    filters.verticals.forEach((vertical) => query.append('vertical', String(vertical)));
  }

  // approvalStatus and requestStatus are arrays; backend uses is_approved and is_close
  if (Array.isArray(filters.approvalStatus) && filters.approvalStatus.length) {
    filters.approvalStatus.forEach((status) => query.append('is_approved', String(status)));
  }
  if (Array.isArray(filters.requestStatus) && filters.requestStatus.length) {
    filters.requestStatus.forEach((status) => query.append('is_close', String(status)));
  }

  // Approvers - send approved_by (name)
  if (includesAssignedToMe) {
    query.append('assigned_to_me', 'true');
  }

  if (namedApprovers.length) {
    namedApprovers.forEach((approver) => query.append('approved_by', String(approver)));
  }

  // Date ranges: choose the active date range in order: approvalDate, requestDate, closedDate
  if (filters.approvalDateFrom && filters.approvalDateTo) {
    query.append('date_type', 'approval_date');
    query.append('from', String(filters.approvalDateFrom));
    query.append('to', String(filters.approvalDateTo));
  } else if (filters.requestDateFrom && filters.requestDateTo) {
    query.append('date_type', 'request_date');
    query.append('from', String(filters.requestDateFrom));
    query.append('to', String(filters.requestDateTo));
  } else if (filters.closedDateFrom && filters.closedDateTo) {
    query.append('date_type', 'closed_date');
    query.append('from', String(filters.closedDateFrom));
    query.append('to', String(filters.closedDateTo));
  }

  // resource role / type (if provided)
  if (filters.resourceTypeIds && Array.isArray(filters.resourceTypeIds) && filters.resourceTypeIds.length) {
    filters.resourceTypeIds.forEach((resourceTypeId) => query.append('resource_type_id', String(resourceTypeId)));
  }


  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
};

export const request = (url, method = 'get', payload = null) => ({
  url,
  method,
  payload,
  headers: { ...defaultHeaders },
  service: serviceName.PROJECT_MS,
  authRequired: true,
  csrfToken: true,
});

export const fetchMetaData = () => {
  return UseAxios(request(STAFFING_REQUEST_META));
};

export const fetchRoleExperienceMappings = (cacheBust = '') => {
  return UseAxios(getRoleExperienceMappings(cacheBust));
};

export const fetchRequests = (filters) => {
  return UseAxios(request(`${STAFFING_REQUEST}${buildQuery(filters)}`));
};

export const approveRequest = (payload) => {
  // payload expected: { srNumber, requestId, status, remarks, roleId }
  const { requestId, status, remarks, ...rest } = payload || {};
  const url = `${STAFFING_REQUEST_APPROVAL_ENDPOINT}/${requestId}/approve`;
  const body = { ...rest, is_approved: Number(status), approved_remarks: remarks };
  return UseAxios(request(url, 'put', body));
};

export const bulkApproveRequest = (payload) => {
  // backend expects PUT /bulk/approve with { ids: [], is_approved, approved_remarks }
  return UseAxios(request(STAFFING_REQUEST_BULK_APPROVAL_ENDPOINT, 'put', payload));
};

export const closeRequest = (payload) => {
  // payload expected: { srNumber, requestId, status, closeDate, remarks }
  const { requestId, status, closeDate, remarks } = payload || {};
  const url = `${STAFFING_REQUEST_CLOSE_ENDPOINT}/${requestId}/close`;
  const body = { is_close: Number(status), close_date: closeDate || null, close_remarks: remarks || null };
  return UseAxios(request(url, 'put', body));
};

export const bulkCloseRequest = (payload) => {
  // backend expects PUT /bulk/close with { ids: [], is_close, close_date, close_remarks }
  return UseAxios(request(STAFFING_REQUEST_BULK_CLOSE_ENDPOINT, 'put', payload));
};

export const createRequest = (payload) => {
  return UseAxios(request(STAFFING_REQUEST_CREATE, 'post', payload));
};

export const deleteRequest = async (requestIdOrPayload) => {
  if (requestIdOrPayload && typeof requestIdOrPayload === 'object') {
    const { requestId, staffingId, ...rest } = requestIdOrPayload;
    const id = staffingId || requestId;
    const url = id ? `${STAFFING_REQUEST}/${id}` : STAFFING_REQUEST;
    const body = { ...rest, staffingId: staffingId ?? requestId ?? id };
    return UseAxios(request(url, 'delete', body));
  }

  const url = `${STAFFING_REQUEST}/${requestIdOrPayload}`;
  return UseAxios(request(url, 'delete', { staffingId: requestIdOrPayload }));
};
